<template>
  <div class="government-employee-container">
    <Govem_bar />

    <v-container fluid class="pa-4 pa-md-8">
      <!-- Header Section -->
      <div class="d-flex flex-wrap align-center justify-space-between mb-6">
        <div>
          <h2 class="font-weight-bold primary--text mb-1">
            <v-icon large color="primary" class="mr-2">mdi-account-tie</v-icon>
            ข้อมูลพนักงานราชการ
          </h2>
          <span class="grey--text text--darken-1 ml-2">
            ระบบบริหารจัดการข้อมูลบุคลากรตามกรอบอัตรากำลัง
          </span>
        </div>
      </div>

      <!-- Statistics Cards -->
      <v-row class="mb-8">
        <v-col cols="12" sm="6" md="6">
          <v-hover v-slot="{ hover }">
            <v-card
              class="rounded-xl overflow-hidden gradient-primary"
              :elevation="hover ? 12 : 4"
              style="cursor: pointer; transition: all 0.3s"
            >
              <v-card-text class="white--text relative pa-6">
                <div class="absolute-circle"></div>
                <div
                  class="d-flex justify-space-between align-start relative z-1"
                >
                  <div>
                    <div class="font-weight-medium">พนักงานทั้งหมด</div>
                    <div class="font-weight-bold mt-2 display-stat">
                      {{ government_employee.length }}
                    </div>
                    <div class="mt-1">คน</div>
                    <div class="mt-3 d-flex gap-3">
                      <div>
                        <div class="text-caption opacity-80">ชาย</div>
                        <div class="font-weight-bold">
                          {{ stats.maleCount }}
                        </div>
                      </div>
                      <div>
                        <div class="text-caption opacity-80">หญิง</div>
                        <div class="font-weight-bold">
                          {{ stats.femaleCount }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <v-avatar color="white" size="64" class="glass-icon">
                    <v-icon size="32" color="primary"
                      >mdi-account-multiple</v-icon
                    >
                  </v-avatar>
                </div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-hover v-slot="{ hover }">
            <v-card
              class="rounded-xl overflow-hidden gradient-success"
              :elevation="hover ? 12 : 4"
              style="cursor: pointer; transition: all 0.3s"
            >
              <v-card-text class="white--text relative pa-6">
                <div class="absolute-circle"></div>
                <div
                  class="d-flex justify-space-between align-start relative z-1"
                >
                  <div>
                    <div class="font-weight-medium">งบประมาณรวม</div>
                    <div
                      class="font-weight-bold mt-2"
                      style="font-size: 1.5rem;"
                    >
                      {{ stats.totalBudget }}
                    </div>
                    <div class="mt-1">เงินเดือนรวม</div>
                    <div class="mt-3 d-flex gap-3">
                      <div>
                        <div class="text-caption opacity-80">เฉลี่ยต่อคน</div>
                        <div
                          class="font-weight-bold"
                          style="font-size: 0.9rem;"
                        >
                          {{ formatCurrency(stats.avgBudget) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <v-avatar color="white" size="64" class="glass-icon">
                    <v-icon size="32" color="success">mdi-cash-multiple</v-icon>
                  </v-avatar>
                </div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>

      <!-- Employee Data Table -->
      <v-card class="rounded-xl" elevation="2">
        <v-toolbar
          flat
          color="white"
          class="px-4 pt-4 pb-2 rounded-t-xl"
          height="auto"
        >
          <v-row class="fill-height align-center">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="searchQuery"
                placeholder="ค้นหา ชื่อ, นามสกุล, ตำแหน่ง..."
                filled
                rounded
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                class="search-bar"
                @input="filterData"
              ></v-text-field>
            </v-col>

            <v-col
              cols="12"
              md="8"
              class="d-flex justify-end align-center flex-wrap gap-2"
            >
              <div class="grey--text mr-2 d-none d-md-block">
                พบข้อมูลทั้งหมด:
                <strong>{{ filteredEmployee.length }}</strong> รายการ
              </div>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="grey darken-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="showToggleHeaders = !showToggleHeaders"
                  >
                    <v-icon>mdi-view-column</v-icon>
                  </v-btn>
                </template>
                <span>เลือกคอลัมน์</span>
              </v-tooltip>

              <v-btn
                v-if="
                  period_college.period_college_enable === '1' && user.user_code
                "
                outlined
                color="primary"
                rounded
                class="text-capitalize"
                @click="openExportDialog"
              >
                <v-icon left>mdi-file-pdf-box</v-icon> ส่งออก PDF
              </v-btn>

              <v-btn
                color="info"
                fab
                small
                dark
                @click="government_employeeQuery()"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-toolbar>

        <v-expand-transition>
          <div
            v-if="showToggleHeaders"
            class="px-6 py-4 grey lighten-5 border-top border-bottom"
          >
            <div class="mb-3 grey--text">แสดง/ซ่อน คอลัมน์ตาราง</div>
            <div class="d-flex flex-wrap gap-2">
              <v-chip
                v-for="header in headersMap"
                :key="header.value"
                :color="
                  selectedHeaders.includes(header.value)
                    ? 'primary'
                    : 'grey lighten-3'
                "
                :text-color="
                  selectedHeaders.includes(header.value)
                    ? 'white'
                    : 'grey darken-1'
                "
                small
                filter
                label
                @click="toggleHeader(header.value)"
                class="font-weight-medium"
              >
                {{ header.text }}
              </v-chip>
            </div>
          </div>
        </v-expand-transition>

        <v-data-table
          :headers="visibleHeaders"
          :items="filteredEmployee"
          :items-per-page="10"
          class="custom-table"
          :loading="loading"
        >
          <template v-slot:item.frist_name="{ item }">
            <div class="d-flex align-center py-3">
              <v-avatar
                color="primary lighten-5"
                size="40"
                class="mr-3 font-weight-bold primary--text"
              >
                {{ item.frist_name ? item.frist_name.charAt(0) : "?" }}
              </v-avatar>
              <div>
                <div
                  class="font-weight-bold text-truncate"
                  style="max-width: 180px;"
                >
                  {{ item.title_s }}{{ item.frist_name }} {{ item.last_name }}
                </div>
                <div class="grey--text">
                  {{ item.id_card || "-" }}
                </div>
              </div>
            </div>
          </template>

          <template v-slot:item.id_card="{ item }">
            <span class="font-mono">{{ item.id_card }}</span>
          </template>

          <template v-slot:item.type_position="{ item }">
            <v-chip
              :color="
                item.type_position === 'ตำแหน่งในภารกิจหลัก'
                  ? 'teal lighten-5'
                  : 'orange lighten-5'
              "
              :text-color="
                item.type_position === 'ตำแหน่งในภารกิจหลัก'
                  ? 'teal darken-2'
                  : 'orange darken-2'
              "
              x-small
              label
              class="font-weight-bold"
            >
              {{ item.type_position }}
            </v-chip>
          </template>

          <template v-slot:item.salary_start="{ item }">
            <span class="grey--text text--darken-1">{{
              formatCurrency(item.salary_start)
            }}</span>
          </template>

          <template v-slot:item.salary_s="{ item }">
            <span class="success--text font-weight-bold">{{
              formatCurrency(item.salary_s)
            }}</span>
          </template>

          <template v-slot:item.appoin_day="{ item }">
            <div>
              {{
                formatThaiDate(
                  item.appoin_year,
                  item.appoin_month,
                  item.appoin_day
                )
              }}
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu
              bottom
              left
              offset-y
              transition="slide-y-transition"
              rounded="lg"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="grey darken-1"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  @click="updateEmployee(item)"
                  v-if="item.id_card !== '0'"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="success">mdi-update</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>จัดการประวัติ</v-list-item-title>
                </v-list-item>

                <v-list-item @click="viewEmployee(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="info">mdi-eye</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>ดูรายละเอียด</v-list-item-title>
                </v-list-item>

                <v-list-item @click="editEmployee(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="warning">mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>แก้ไขข้อมูล</v-list-item-title>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item @click="confirmDelete(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="error--text"
                    >ลบข้อมูล</v-list-item-title
                  >
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-8">
              <v-icon size="64" color="grey lighten-3"
                >mdi-account-search-outline</v-icon
              >
              <p class="grey--text text--darken-1 mt-2">ไม่พบข้อมูลพนักงาน</p>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <div
        v-if="period_college.period_college_enable === '1' && user.user_code"
        class="mt-4 mt-md-0"
      >
        <Goverment_employee_report
          v-if="government_employee.length > 0"
          :period_college="period_college"
          :user="user"
          :gov_employee_count="gov_employee_count"
          :count_manpower="government_employee.length"
        />
      </div>


      <div>
        <ReportBudgetHistory v-if="user.user_code > 0" :user="user" :period_college="period_college" />
      </div>
    </v-container>

    <!-- Add/Edit Dialog -->
    <v-dialog
      v-model="showAddDialog"
      max-width="80%"
      scrollable
      transition="dialog-bottom-transition"
    >
      <v-card class="rounded-xl">
        <v-toolbar :color="editingId ? 'warning' : 'primary'" dark flat class="rounded-t-xl">
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="font-weight-bold pl-0">
            {{ editingId ? "แก้ไขข้อมูลพนักงาน" : "ลงทะเบียนพนักงานใหม่" }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            text
            class="text-capitalize font-weight-bold"
            @click="saveEmployee"
            :loading="isSubmitting"
            :disabled="!formValid || isUploading"
          >
            <v-icon left>mdi-content-save</v-icon> บันทึก
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0 bg-light-grey">
          <v-form
            ref="employeeForm"
            v-model="formValid"
            lazy-validation
            class="pa-4 pa-md-6"
          >
            <v-row>
              <v-col cols="12" md="7">
                <!-- Personal Information -->
                <v-card class="mb-4 rounded-lg" outlined>
                  <v-card-title
                    class="text-body-1 font-weight-bold py-3 px-4 grey lighten-4"
                  >
                    <v-icon left color="success">mdi-account-box</v-icon>
                    ข้อมูลส่วนตัว
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-4">
                    <v-row dense>
                      <v-col cols="12">
                        <v-text-field
                          v-model="formData.id_card"
                          label="เลขบัตรประชาชน (13 หลัก)"
                          outlined
                          dense
                          counter="13"
                          maxlength="13"
                          :rules="idCardRules"
                          class="rounded-input"
                          @input="validateIdCard"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-autocomplete
                          v-model="formData.title_s"
                          :items="prefix"
                          label="คำนำหน้า"
                          outlined
                          dense
                          class="rounded-input"
                          item-text="prefix_name"
                          item-value="prefix_name"
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="formData.frist_name"
                          label="ชื่อจริง"
                          outlined
                          dense
                          :rules="[v => !!v || 'กรุณากรอก']"
                          class="rounded-input"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="5">
                        <v-text-field
                          v-model="formData.last_name"
                          label="นามสกุล"
                          outlined
                          dense
                          :rules="[v => !!v || 'กรุณากรอก']"
                          class="rounded-input"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="formData.sex"
                          :items="sexOptions"
                          label="เพศ"
                          outlined
                          dense
                          class="rounded-input"
                        ></v-select>
                      </v-col>
                      
                      <!-- วันเกิด แบบ Dropdown -->
                      <v-col cols="12">
                        <div class="text-subtitle-2 grey--text mb-2">วันเกิด</div>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-select
                          v-model="formData.brith_day"
                          :items="getDayOptions('birth')"
                          label="วัน"
                          outlined
                          dense
                          :rules="[v => !!v || 'กรุณาเลือกวัน']"
                          class="rounded-input"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="5">
                        <v-select
                          v-model="formData.brith_month"
                          :items="monthOptions"
                          item-text="text"
                          item-value="value"
                          label="เดือน"
                          outlined
                          dense
                          :rules="[v => !!v || 'กรุณาเลือกเดือน']"
                          class="rounded-input"
                          @change="onBirthMonthYearChange"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="formData.brith_year"
                          :items="birthYearOptions"
                          label="ปี (พ.ศ.)"
                          outlined
                          dense
                          clearable
                          :rules="birthYearRules"
                          class="rounded-input"
                          @change="onBirthMonthYearChange"
                        ></v-select>
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="formData.marital_status"
                          :items="maritalStatus"
                          label="สถานภาพ"
                          outlined
                          dense
                          class="rounded-input"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="formData.province"
                          :items="province"
                          item-text="province_name"
                          item-value="province_ID"
                          label="จังหวัดภูมิลำเนา"
                          outlined
                          dense
                          class="rounded-input"
                        ></v-autocomplete>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <!-- Education Information -->
                <v-card class="rounded-lg" outlined>
                  <v-card-title
                    class="text-body-1 font-weight-bold py-3 px-4 grey lighten-4"
                  >
                    <v-icon left color="warning">mdi-school</v-icon>
                    ประวัติการศึกษา
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-4">
                    <v-row dense>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="formData.level_edd"
                          :items="educationLevels"
                          label="ระดับ"
                          outlined
                          dense
                          class="rounded-input"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model="formData.ed_abb"
                          label="วุฒิย่อ (เช่น วศ.บ., บธ.บ.)"
                          outlined
                          dense
                          class="rounded-input"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="formData.ed_name"
                          label="สาขาวิชาเอก"
                          outlined
                          dense
                          class="rounded-input"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="formData.edd_university"
                          label="สถาบันการศึกษา"
                          outlined
                          dense
                          class="rounded-input"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="5">
                <!-- Position Information -->
                <v-card class="mb-4 rounded-lg" outlined>
                  <v-card-title
                    class="text-body-1 font-weight-bold py-3 px-4 grey lighten-4"
                  >
                    <v-icon left color="primary">mdi-briefcase</v-icon>
                    ข้อมูลตำแหน่ง
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-4">
                    <v-text-field
                      v-model="formData.college_code"
                      label="รหัสวิทยาลัย"
                      outlined
                      dense
                      disabled
                      class="rounded-input mb-2"
                    ></v-text-field>
                    <v-text-field
                      v-model="formData.id_position"
                      label="เลขที่ตำแหน่ง"
                      outlined
                      dense
                      disabled
                      class="rounded-input mb-2"
                    ></v-text-field>
                    <v-text-field
                      v-model="formData.type_position"
                      label="ประเภทตำแหน่ง"
                      outlined
                      dense
                      disabled
                      class="rounded-input mb-2"
                    ></v-text-field>
                    <v-text-field
                      v-model="formData.name_postion"
                      label="ชื่อตำแหน่ง"
                      outlined
                      dense
                      disabled
                      class="rounded-input"
                      background-color="grey lighten-5"
                    ></v-text-field>
                  </v-card-text>
                </v-card>

                <!-- Contract & Salary Information -->
                <v-card class="rounded-lg" outlined>
                  <v-card-title
                    class="text-body-1 font-weight-bold py-3 px-4 grey lighten-4"
                  >
                    <v-icon left color="info">mdi-file-document-edit</v-icon>
                    สัญญาจ้าง & เงินเดือน
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-4">
                    <!-- วันแรกบรรจุ แบบ Dropdown -->
                    <div class="text-subtitle-2 grey--text mb-2">วันแรกบรรจุ</div>
                    <v-row dense>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="formData.appoin_day"
                          :items="getDayOptions('appointment')"
                          label="วัน"
                          outlined
                          dense
                          :rules="[v => !!v || 'กรุณาเลือกวัน']"
                          class="rounded-input"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="formData.appoin_month"
                          :items="monthOptions"
                          item-text="text"
                          item-value="value"
                          label="เดือน"
                          outlined
                          dense
                          :rules="[v => !!v || 'กรุณาเลือกเดือน']"
                          class="rounded-input"
                          @change="onAppointmentMonthYearChange"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="formData.appoin_year"
                          :items="appointmentYearOptions"
                          label="ปี (พ.ศ.)"
                          outlined
                          dense
                          clearable
                          :rules="appointmentYearRules"
                          class="rounded-input"
                          @change="onAppointmentMonthYearChange"
                        ></v-select>
                      </v-col>
                    </v-row>

                    <v-row dense class="mt-2">
                      <v-col cols="6">
                        <v-text-field
                          v-model="formData.salary_start"
                          label="เงินเดือนแรกบรรจุ"
                          prefix="฿"
                          type="number"
                          outlined
                          dense
                          :rules="salaryRules"
                          class="rounded-input"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="formData.salary_s"
                          label="เงินเดือนปัจจุบัน"
                          prefix="฿"
                          type="number"
                          outlined
                          dense
                          :rules="currentSalaryRules"
                          class="rounded-input"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <div class="grey--text mb-2 mt-4">สัญญาจ้างปัจจุบัน</div>
                    <v-row dense>
                      <v-col cols="6">
                        <v-menu
                          v-model="datePicker.contractStart"
                          :close-on-content-click="false"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="contractStartFormatted"
                              label="วันเริ่มสัญญา"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              class="rounded-input"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="formData.last_contract_start"
                            locale="th"
                            @input="onContractStartChange"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="6">
                        <v-menu
                          v-model="datePicker.contractEnd"
                          :close-on-content-click="false"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="contractEndFormatted"
                              label="วันสิ้นสุด"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              :rules="formData.last_contract_start ? contractEndRules : []"
                              class="rounded-input"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="formData.last_contract_end"
                            locale="th"
                            :min="formData.last_contract_start"
                            @input="datePicker.contractEnd = false"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>

                    <v-file-input
                      v-model="govem_contract_file"
                      label="แนบไฟล์สัญญาจ้าง (PDF)"
                      outlined
                      dense
                      prepend-icon=""
                      prepend-inner-icon="mdi-paperclip"
                      accept=".pdf"
                      class="rounded-input mt-2"
                      :loading="isUploading"
                      show-size
                    ></v-file-input>
                    <div
                      v-if="formData.govem_contract"
                      class="d-flex align-center mt-1 pa-2 green lighten-5 rounded"
                    >
                      <v-icon color="success" small class="mr-2"
                        >mdi-check-circle</v-icon
                      >
                      <span class="green--text text--darken-2"
                        >มีไฟล์เดิม: {{ formData.govem_contract }}</span
                      >
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            class="text-capitalize font-weight-bold"
            @click="saveEmployee"
            :loading="isSubmitting"
            :disabled="!formValid || isUploading"
          >
            <v-icon left>mdi-content-save</v-icon> บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Dialog -->
    <v-dialog
      v-model="showViewDialog"
      max-width="500px"
      transition="dialog-bottom-transition"
    >
      <v-card class="rounded-xl overflow-hidden" v-if="viewingEmployee">
        <div class="gradient-primary pa-6 white--text text-center relative">
          <div class="absolute-circle"></div>
          <v-avatar size="90" color="white" class="elevation-3 mb-3">
            <span class="primary--text font-weight-bold">{{
              viewingEmployee.frist_name.charAt(0)
            }}</span>
          </v-avatar>
          <h3 class="text-h6 font-weight-bold relative z-1">
            {{ viewingEmployee.title_s }}{{ viewingEmployee.frist_name }}
            {{ viewingEmployee.last_name }}
          </h3>
          <div class="relative z-1">
            {{ viewingEmployee.name_postion }}
          </div>
        </div>

        <v-card-text class="pa-6">
          <v-row dense class="mb-4">
            <v-col cols="6">
              <div class="grey--text">เลขบัตรประชาชน</div>
              <div class="font-weight-medium">
                {{ viewingEmployee.id_card || "-" }}
              </div>
            </v-col>
            <v-col cols="6">
              <div class="grey--text">สถานภาพ</div>
              <div class="font-weight-medium">
                {{ viewingEmployee.marital_status || "-" }}
              </div>
            </v-col>
            <v-col cols="6" class="mt-2">
              <div class="grey--text">วันเกิด</div>
              <div class="font-weight-medium">
                {{
                  formatThaiDate(
                    viewingEmployee.brith_year,
                    viewingEmployee.brith_month,
                    viewingEmployee.brith_day
                  )
                }}
              </div>
            </v-col>
            <v-col cols="6" class="mt-2">
              <div class="grey--text">ภูมิลำเนา</div>
              <div class="font-weight-medium">
                {{ viewingEmployee.province_name || viewingEmployee.province || "-" }}
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-3 dashed"></v-divider>

          <div class="font-weight-bold primary--text mb-2">การศึกษา</div>
          <div class="d-flex align-center mb-2">
            <v-icon small color="primary" class="mr-2">mdi-school</v-icon>
            <span
              >{{ viewingEmployee.ed_abb }} {{ viewingEmployee.ed_name }}</span
            >
          </div>
          <div class="d-flex align-center">
            <v-icon small color="grey" class="mr-2">mdi-domain</v-icon>
            <span class="grey--text">{{
              viewingEmployee.edd_university || "ไม่ระบุสถาบัน"
            }}</span>
          </div>

          <v-divider class="my-3 dashed"></v-divider>

          <div class="font-weight-bold primary--text mb-2">
            ข้อมูลการจ้างงาน
          </div>
          <v-row dense>
            <v-col cols="6">
              <div class="pa-3 grey lighten-5 rounded-lg text-center">
                <div class="grey--text">เงินเดือนปัจจุบัน</div>
                <div class="text-h6 success--text font-weight-bold">
                  {{ formatCurrency(viewingEmployee.salary_s) }}
                </div>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="pa-3 grey lighten-5 rounded-lg text-center">
                <div class="grey--text">เริ่มสัญญา</div>
                <div class="font-weight-bold mt-1">
                  {{
                    formatThaiDate(
                      viewingEmployee.appoin_year,
                      viewingEmployee.appoin_month,
                      viewingEmployee.appoin_day
                    ) || "-"
                  }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-btn
            v-if="viewingEmployee.govem_contract"
            block
            outlined
            color="primary"
            class="mt-4 rounded-lg"
            :href="'../HRemployee_contract/' + viewingEmployee.govem_contract"
            target="_blank"
          >
            <v-icon left>mdi-file-document</v-icon> ดาวน์โหลดสัญญาจ้าง
          </v-btn>
        </v-card-text>
        <v-card-actions class="pb-6 px-6 pt-0">
          <v-btn
            block
            color="grey lighten-3"
            depressed
            rounded
            class="grey--text text--darken-2"
            @click="showViewDialog = false"
          >
            ปิด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="360">
      <v-card class="rounded-xl text-center pa-6">
        <div class="mb-4">
          <v-avatar color="red lighten-5" size="80">
            <v-icon size="40" color="error">mdi-alert-circle-outline</v-icon>
          </v-avatar>
        </div>
        <h3 class="text-h6 font-weight-bold mb-2">ยืนยันการลบข้อมูล?</h3>
        <p class="grey--text mb-6">
          คุณต้องการลบข้อมูลของ <br />
          <strong class="black--text">
            {{ deletingEmployee.title_s }}{{ deletingEmployee.frist_name }}
            {{ deletingEmployee.last_name }}
          </strong>
          <br />
          ใช่หรือไม่? การกระทำนี้ไม่สามารถยกเลิกได้
        </p>
        <div class="d-flex justify-center gap-2">
          <v-btn
            depressed
            rounded
            color="grey lighten-3"
            @click="showDeleteDialog = false"
            class="px-6"
          >
            ยกเลิก
          </v-btn>
          <v-btn
            depressed
            rounded
            color="error"
            @click="deleteEmployee()"
            :loading="isDeleting"
            class="px-6"
          >
            ลบข้อมูล
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Export Dialog -->
    <v-dialog v-model="showExportDialog" max-width="550px">
      <v-card class="rounded-xl">
        <v-card-title class="font-weight-bold grey lighten-5 pa-4">
          <v-icon color="error" class="mr-2">mdi-file-pdf-box</v-icon>
          รายงานข้อมูลบุคลากร (PDF)
        </v-card-title>
        <v-card-text class="pa-6">
          <v-form ref="exportForm" v-model="exportFormValid">
            <div class="mb-2">ข้อมูลหัวกระดาษ</div>
            <v-text-field
              v-model="exportOptions.collegeName"
              label="ชื่อหน่วยงาน/วิทยาลัย"
              outlined
              dense
              class="rounded-input mb-3"
              :rules="[v => !!v || 'จำเป็นต้องกรอก']"
            ></v-text-field>

            <div class="mb-2">ข้อมูลผู้ลงนาม</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="exportOptions.directorName"
                  label="ชื่อผู้อำนวยการ"
                  outlined
                  dense
                  class="rounded-input"
                  :rules="[v => !!v || 'จำเป็นต้องกรอก']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="exportOptions.directorTitle"
                  label="ตำแหน่ง"
                  outlined
                  dense
                  class="rounded-input"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="mt-4 pa-4 rounded-lg border-dashed bg-light-grey">
              <v-checkbox
                v-model="exportOptions.includeStatistics"
                label="แนบหน้าสรุปสถิติอัตรากำลัง"
                dense
                hide-details
                class="mt-0"
              ></v-checkbox>
              <v-checkbox
                v-model="exportOptions.includeSignature"
                label="แสดงส่วนลงนามท้ายกระดาษ"
                dense
                hide-details
              ></v-checkbox>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="closeExportDialog">ยกเลิก</v-btn>
          <v-btn
            color="primary"
            rounded
            depressed
            @click="exportToPDF"
            :loading="exportLoading"
            :disabled="!exportFormValid"
          >
            <v-icon left>mdi-download</v-icon> ดาวน์โหลด PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Report Viewer -->
    <ReportViewer
      :report_govem_pdf="report_govem_pdf"
      v-if="showReportViewer"
      @close="closeReportViewer"
    />

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      rounded="pill"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon small class="mr-2" color="white">
          {{
            snackbar.color === "success"
              ? "mdi-check-circle"
              : "mdi-alert-circle"
          }}
        </v-icon>
        {{ snackbar.message }}
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import Govem_bar from "../../../components/college/govem_bar_iven.vue";
import Goverment_employee_report from "../../../components/college/goverment_employee_report.vue";
import ReportViewer from "../../../components/college/reportViewer.vue";
import ReportBudgetHistory from "../../../components/college/reportBudgetHistory.vue";
export default {
  components: {
    Govem_bar,
    Goverment_employee_report,
    ReportViewer,
    ReportBudgetHistory
  },
  data() {
    return {
      ApiKey: "HRvec2021",
      government_employee: [],
      filteredEmployee: [],
      user: [],

      // UI State
      showAddDialog: false,
      showViewDialog: false,
      showDeleteDialog: false,
      showToggleHeaders: false,
      showExportDialog: false,
      showReportViewer: false,

      // Form Data
      formValid: false,
      exportFormValid: false,
      editingId: null,
      isSubmitting: false,
      isDeleting: false,
      isUploading: false,
      exportLoading: false,
      searchQuery: "",

      // Export Options
      exportOptions: {
        includeStatistics: true,
        includeSignature: true,
        directorName: "",
        directorTitle: "ผู้อำนวยการวิทยาลัย",
        collegeName: ""
      },

      // File Upload
      govem_contract_file: null,

      // Date Pickers
      datePicker: {
        birth: false,
        appointment: false,
        contractStart: false,
        contractEnd: false
      },

      // Viewing/Deleting
      viewingEmployee: null,
      deletingEmployee: {},

      // Form Data Structure
      formData: {
        college_code: "",
        id_position: "",
        title_s: "",
        frist_name: "",
        last_name: "",
        id_card: "",
        sex: "",
        brith_date: "",
        brith_day: null,
        brith_month: null,
        brith_year: null,
        name_postion: "",
        type_position: "",
        marital_status: "",
        level_edd: "",
        ed_abb: "",
        ed_name: "",
        edd_university: "",
        appoin_date: "",
        appoin_day: null,
        appoin_month: null,
        appoin_year: null,
        salary_start: "",
        last_contract_start: "",
        last_contract_end: "",
        salary_s: "",
        govem_contract: "",
        province: ""
      },

      // Headers Configuration
      headersMap: [
        { text: "จัดการ", align: "center", value: "actions", sortable: false },
        { text: "ชื่อ-นามสกุล", align: "start", value: "frist_name" },
        { text: "เลขที่ตำแหน่ง", align: "start", value: "id_position" },
        { text: "เลขบัตรฯ", align: "start", value: "id_card" },
        { text: "ตำแหน่ง", align: "start", value: "name_postion" },
        { text: "วันที่บรรจุครั้งแรก", align: "start", value: "appoin_day" },
        { text: "ประเภท", align: "center", value: "type_position" },
        { text: "เงินเดือนบรรจุ", align: "end", value: "salary_start" },
        {
          text: "เงินเดือนปัจจุบัน",
          align: "end",
          value: "salary_s"
        },
        { text: "วุฒิ", align: "center", value: "ed_abb" }
      ],
      selectedHeaders: [
        "frist_name",
        "name_postion",
        "id_position",
        "appoin_day",
        "type_position",
        "salary_start",
        "salary_s",
        "actions"
      ],

      // Dropdown Options
      titles: ["นาย", "นาง", "นางสาว", "ว่าที่ร้อยตรี", "อื่น ๆ"],
      sexOptions: ["ชาย", "หญิง"],
      maritalStatus: ["โสด", "สมรส", "หม้าย", "หย่า"],
      educationLevels: ["ปริญญาตรี", "ปริญญาโท", "ปริญญาเอก"],
      
      // Month Options
      monthOptions: [
        { text: "มกราคม", value: 1 },
        { text: "กุมภาพันธ์", value: 2 },
        { text: "มีนาคม", value: 3 },
        { text: "เมษายน", value: 4 },
        { text: "พฤษภาคม", value: 5 },
        { text: "มิถุนายน", value: 6 },
        { text: "กรกฎาคม", value: 7 },
        { text: "สิงหาคม", value: 8 },
        { text: "กันยายน", value: 9 },
        { text: "ตุลาคม", value: 10 },
        { text: "พฤศจิกายน", value: 11 },
        { text: "ธันวาคม", value: 12 }
      ],

      // Validation Rules
      idCardRules: [
        v => !!v || 'กรุณากรอกเลขบัตรประชาชน',
        v => (v && v.length === 13) || 'เลขบัตรประชาชนต้องมี 13 หลัก',
        v => /^\d+$/.test(v) || 'เลขบัตรประชาชนต้องเป็นตัวเลขเท่านั้น',
        v => this.validateThaiID(v) || 'เลขบัตรประชาชนไม่ถูกต้อง'
      ],
      
      birthYearRules: [
        v => !!v || 'กรุณาเลือกปีเกิด',
        v => this.validateAge(v) || 'อายุต้องอยู่ระหว่าง 18-65 ปี'
      ],
      
      appointmentYearRules: [
        v => !!v || 'กรุณาเลือกปีบรรจุ',
        v => this.validateAppointmentYear(v) || 'ปีบรรจุต้องไม่เกินปีปัจจุบัน'
      ],
      
      salaryRules: [
        v => !!v || 'กรุณากรอกเงินเดือน',
        v => (v && parseFloat(v) > 0) || 'เงินเดือนต้องมากกว่า 0',
        v => (v && parseFloat(v) <= 200000) || 'เงินเดือนต้องไม่เกิน 200,000 บาท'
      ],
      
      currentSalaryRules: [
        v => !!v || 'กรุณากรอกเงินเดือนปัจจุบัน',
        v => (v && parseFloat(v) > 0) || 'เงินเดือนต้องมากกว่า 0',
        v => (v && parseFloat(v) <= 200000) || 'เงินเดือนต้องไม่เกิน 200,000 บาท',
        v => this.validateCurrentSalary(v) || 'เงินเดือนปัจจุบันต้องมากกว่าหรือเท่ากับเงินเดือนแรกบรรจุ'
      ],
      
      contractEndRules: [
        v => {
          // ถ้ายังไม่มีวันสิ้นสุด ก็ไม่ต้อง validate
          if (!v) return true;
          // ถ้ายังไม่มีวันเริ่มต้น ก็ยังไม่สามารถ validate ได้
          if (!this.formData.last_contract_start) return true;
          // มีทั้งสองวัน ตรวจสอบว่าวันสิ้นสุด > วันเริ่มต้น
          return this.validateContractEnd(v) || 'วันสิ้นสุดสัญญาต้องมากกว่าวันเริ่มสัญญา';
        }
      ],

      // Snackbar
      snackbar: {
        show: false,
        message: "",
        color: "success",
        timeout: 3000
      },

      // Statistics
      stats: {
        maleCount: 0,
        femaleCount: 0,
        educationTypes: 0,
        totalBudget: 0,
        avgBudget: 0
      },

      year_select: [],
      prefix: [],
      education_levels: [],
      report_govem_pdf: {},
      period_college: [],
      gov_employee_count: "",
      loading: false,
      province:[]
    };
  },

  computed: {
    visibleHeaders() {
      return this.headersMap.filter(h =>
        this.selectedHeaders.includes(h.value)
      );
    },

    // Year Options
    birthYearOptions() {
      const currentYear = new Date().getFullYear() + 543;
      const minYear = currentYear - 65; // อายุสูงสุด 65 ปี
      const maxYear = currentYear - 18; // อายุต่ำสุด 18 ปี
      const years = [];
      
      // เพิ่มปีปัจจุบันที่มีในข้อมูล (ถ้ามี) เพื่อให้แสดงผลได้
      if (this.formData.brith_year && 
          (this.formData.brith_year < minYear || this.formData.brith_year > maxYear)) {
        years.push(parseInt(this.formData.brith_year));
      }
      
      // สร้างรายการปีตามปกติ
      for (let year = maxYear; year >= minYear; year--) {
        years.push(year);
      }
      
      // เรียงลำดับใหม่
      return years.sort((a, b) => b - a);
    },

    appointmentYearOptions() {
      const currentYear = new Date().getFullYear() + 543;
      const startYear = currentYear - 50; // ขยายเป็น 50 ปี
      const years = [];
      
      // เพิ่มปีปัจจุบันที่มีในข้อมูล (ถ้ามี) เพื่อให้แสดงผลได้
      if (this.formData.appoin_year && this.formData.appoin_year < startYear) {
        years.push(parseInt(this.formData.appoin_year));
      }
      
      // สร้างรายการปีตามปกติ
      for (let year = currentYear; year >= startYear; year--) {
        years.push(year);
      }
      
      // เรียงลำดับใหม่
      return years.sort((a, b) => b - a);
    },

    birthDateFormatted() {
      return this.formData.brith_date
        ? this.formatThaiDate(
            new Date(this.formData.brith_date).getFullYear() + 543,
            new Date(this.formData.brith_date).getMonth() + 1,
            new Date(this.formData.brith_date).getDate()
          )
        : "";
    },

    appointmentDateFormatted() {
      return this.formData.appoin_date
        ? this.formatThaiDate(
            new Date(this.formData.appoin_date).getFullYear() + 543,
            new Date(this.formData.appoin_date).getMonth() + 1,
            new Date(this.formData.appoin_date).getDate()
          )
        : "";
    },

    contractStartFormatted() {
      return this.formData.last_contract_start
        ? this.formatThaiDate(
            new Date(this.formData.last_contract_start).getFullYear() + 543,
            new Date(this.formData.last_contract_start).getMonth() + 1,
            new Date(this.formData.last_contract_start).getDate()
          )
        : "";
    },

    contractEndFormatted() {
      return this.formData.last_contract_end
        ? this.formatThaiDate(
            new Date(this.formData.last_contract_end).getFullYear() + 543,
            new Date(this.formData.last_contract_end).getMonth() + 1,
            new Date(this.formData.last_contract_end).getDate()
          )
        : "";
    },

    malePercentage() {
      const total = this.government_employee.length;
      return total > 0 ? Math.round((this.stats.maleCount / total) * 100) : 0;
    },

    femalePercentage() {
      const total = this.government_employee.length;
      return total > 0 ? Math.round((this.stats.femaleCount / total) * 100) : 0;
    }
  },

  async mounted() {
    try {
      this.loading = true;
      await this.period_collegeQuery();
      await this.provinceQuery();
      await this.yearSelectQuery();
      await this.education_levelsQuery();
      await this.prefixQuery();
      await this.userQuery();
      await this.government_employeeQuery();
      this.initializeData();
    } finally {
      this.loading = false;
    }
  },

  methods: {
    async provinceQuery(){
      let result = await this.$http.post('province.php',{
        ApiKey:this.ApiKey
      })
      this.province = result.data
    },
    // ==================== Day Options ====================
    getDayOptions(type) {
      let maxDays = 31;
      
      if (type === 'birth' && this.formData.brith_month && this.formData.brith_year) {
        maxDays = this.getDaysInMonth(this.formData.brith_month, this.formData.brith_year);
      } else if (type === 'appointment' && this.formData.appoin_month && this.formData.appoin_year) {
        maxDays = this.getDaysInMonth(this.formData.appoin_month, this.formData.appoin_year);
      }
      
      const days = [];
      for (let i = 1; i <= maxDays; i++) {
        days.push(i);
      }
      return days;
    },

    getDaysInMonth(month, buddhistYear) {
      // แปลง พ.ศ. เป็น ค.ศ.
      const gregorianYear = parseInt(buddhistYear) - 543;
      // month เป็น 1-12, แต่ JavaScript ใช้ 0-11
      return new Date(gregorianYear, parseInt(month), 0).getDate();
    },

    onBirthMonthYearChange() {
      // ตรวจสอบว่าวันที่เลือกไว้เกินจำนวนวันในเดือนหรือไม่
      if (this.formData.brith_day && this.formData.brith_month && this.formData.brith_year) {
        const maxDays = this.getDaysInMonth(this.formData.brith_month, this.formData.brith_year);
        if (parseInt(this.formData.brith_day) > maxDays) {
          this.formData.brith_day = maxDays;
        }
      }
    },

    onAppointmentMonthYearChange() {
      // ตรวจสอบว่าวันที่เลือกไว้เกินจำนวนวันในเดือนหรือไม่
      if (this.formData.appoin_day && this.formData.appoin_month && this.formData.appoin_year) {
        const maxDays = this.getDaysInMonth(this.formData.appoin_month, this.formData.appoin_year);
        if (parseInt(this.formData.appoin_day) > maxDays) {
          this.formData.appoin_day = maxDays;
        }
      }
    },

    onContractStartChange() {
      this.datePicker.contractStart = false;
      // Validate form เมื่อเลือกวันเริ่มสัญญา
      this.$nextTick(() => {
        if (this.$refs.employeeForm) {
          this.$refs.employeeForm.validate();
        }
      });
    },

    // ==================== Validation Methods ====================
    validateThaiID(id) {
      if (!id || id.length !== 13) return false;
      
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += parseInt(id.charAt(i)) * (13 - i);
      }
      
      const mod = sum % 11;
      const checkDigit = (11 - mod) % 10;
      
      return checkDigit === parseInt(id.charAt(12));
    },

    validateIdCard() {
      // Real-time validation for ID card
      if (this.formData.id_card && this.formData.id_card.length === 13) {
        if (!this.validateThaiID(this.formData.id_card)) {
          this.showSnackbar('เลขบัตรประชาชนไม่ถูกต้อง', 'warning');
        }
      }
    },

    validateAge(buddhistYear) {
      if (!buddhistYear) return true;
      
      const currentYear = new Date().getFullYear() + 543;
      const age = currentYear - parseInt(buddhistYear);
      
      return age >= 18 && age <= 65;
    },

    validateAppointmentYear(buddhistYear) {
      if (!buddhistYear) return true;
      
      const currentYear = new Date().getFullYear() + 543;
      return parseInt(buddhistYear) <= currentYear;
    },

    validateCurrentSalary(currentSalary) {
      if (!currentSalary || !this.formData.salary_start) return true;
      
      return parseFloat(currentSalary) >= parseFloat(this.formData.salary_start);
    },

    validateContractEnd(endDate) {
      // ถ้าไม่มีวันสิ้นสุด หรือไม่มีวันเริ่มต้น ให้ผ่าน
      if (!endDate || !this.formData.last_contract_start) return true;
      
      try {
        // แปลง string date เป็น Date object
        const startDate = new Date(this.formData.last_contract_start);
        const end = new Date(endDate);
        
        // ตรวจสอบว่า Date object ถูกต้อง
        if (isNaN(startDate.getTime()) || isNaN(end.getTime())) {
          console.warn('Invalid date format:', { start: this.formData.last_contract_start, end: endDate });
          return true; // ถ้าวันที่ไม่ถูกต้อง ให้ผ่านไปก่อน
        }
        
        // เปรียบเทียบ timestamp
        const isValid = end.getTime() > startDate.getTime();
        
        // Debug
        console.log('Contract Date Validation:', {
          start: this.formData.last_contract_start,
          end: endDate,
          startDate: startDate.toISOString(),
          endDate: end.toISOString(),
          startTimestamp: startDate.getTime(),
          endTimestamp: end.getTime(),
          difference: (end.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24), // วัน
          isValid: isValid
        });
        
        return isValid;
      } catch (error) {
        console.error('Error validating contract end date:', error);
        return true; // ถ้าเกิดข้อผิดพลาด ให้ผ่านไปก่อน
      }
    },

    // ==================== Data Query Methods ====================
    async period_collegeQuery() {
      try {
        let result = await this.$http.post("period_college.php", {
          ApiKey: this.ApiKey,
          period_college_id: "80104"
        });
        this.period_college = result.data;
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลช่วงการศึกษา", "error");
      }
    },

    async education_levelsQuery() {
      try {
        let result = await this.$http.post("education_levels.php", {
          ApiKey: this.ApiKey
        });
        this.education_levels = result.data;
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดระดับการศึกษา", "error");
      }
    },

    async prefixQuery() {
      try {
        let result = await this.$http.post("prefix.php", {
          ApiKey: this.ApiKey
        });
        this.prefix = result.data;
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดคำนำหน้า", "error");
      }
    },

    yearSelectQuery() {
      const currentYear = new Date().getFullYear() + 543;
      for (let i = 0; i <= 40; i++) {
        this.year_select.push(currentYear - i);
      }
    },

    async userQuery() {
      try {
        const userSession = JSON.parse(sessionStorage.getItem("user")) || {};
        const resultUser = await this.$http.post("user.php", {
          ApiKey: this.ApiKey,
          user_ID: userSession.user_ID
        });
        this.user = resultUser.data;
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้", "error");
      }
    },

    async government_employeeQuery() {
      try {
        const result = await this.$http.post("government_employee.php", {
          ApiKey: this.ApiKey,
          college_code: this.user.user_code || ""
        });
        this.government_employee = Array.isArray(result.data)
          ? result.data
          : [];
        this.filteredEmployee = [...this.government_employee];
        this.gov_employee_count = this.government_employee.filter(
          value => value.id_card != 0
        ).length;
        this.updateStatistics();
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
      }
    },

    initializeData() {
      this.updateStatistics();
    },

    updateStatistics() {
      this.stats.maleCount = this.government_employee.filter(
        e => e.sex === "ชาย"
      ).length;
      this.stats.femaleCount = this.government_employee.filter(
        e => e.sex === "หญิง"
      ).length;
      this.stats.educationTypes = [
        ...new Set(this.government_employee.map(e => e.ed_abb).filter(Boolean))
      ].length;

      // Calculate budget
      this.stats.totalBudget = this.government_employee.reduce((sum, emp) => {
        return sum + parseFloat(emp.salary_s || 0);
      }, 0);

      this.stats.avgBudget =
        this.government_employee.length > 0
          ? this.stats.totalBudget / this.government_employee.length
          : 0;
    },

    filterData() {
      if (!this.searchQuery) {
        this.filteredEmployee = [...this.government_employee];
        return;
      }

      const query = this.searchQuery.toLowerCase();
      this.filteredEmployee = this.government_employee.filter(
        e =>
          (e.frist_name && e.frist_name.toLowerCase().includes(query)) ||
          (e.last_name && e.last_name.toLowerCase().includes(query)) ||
          (e.name_postion && e.name_postion.toLowerCase().includes(query)) ||
          (e.id_card && e.id_card.toString().includes(query))
      );
    },

    toggleHeader(value) {
      const index = this.selectedHeaders.indexOf(value);
      if (index > -1) {
        this.selectedHeaders.splice(index, 1);
      } else {
        this.selectedHeaders.push(value);
      }
    },

    openExportDialog() {
      if (this.filteredEmployee.length === 0) {
        this.showSnackbar("ไม่มีข้อมูลในการส่งออก", "warning");
        return;
      }

      this.exportOptions.directorName = this.user.collegeinfo_director || "";
      this.exportOptions.collegeName =
        this.user.college_name || "วิทยาลัยเทคนิค";
      this.showExportDialog = true;
    },

    async exportToPDF() {
      if (!this.$refs.exportForm.validate()) {
        this.showSnackbar("กรุณากรอกข้อมูลให้ครบถ้วน", "warning");
        return;
      }

      this.exportLoading = true;

      try {
        const employees = this.filteredEmployee.map(emp => ({
          ...emp,
          full_name: `${emp.title_s || ""} ${emp.frist_name ||
            ""} ${emp.last_name || ""}`.trim()
        }));

        const payload = {
          ApiKey: this.ApiKey,
          college_code: this.user.user_code || "",
          college_name: this.exportOptions.collegeName,
          director_name: this.exportOptions.directorName,
          director_title: this.exportOptions.directorTitle,
          report_date: new Date().toISOString().split("T")[0],
          employees: JSON.stringify(employees),
          include_statistics: this.exportOptions.includeStatistics ? "1" : "0",
          include_signature: this.exportOptions.includeSignature ? "1" : "0"
        };

        let response = await this.$http.post(
          "export_goverment_employee_pdf.php",
          payload
        );

        if (response.data && response.data.success) {
          this.report_govem_pdf = response.data;
          this.showReportViewer = true;
          this.showSnackbar("ส่งออก PDF สำเร็จ", "success");
          this.showExportDialog = false;
        } else {
          this.showSnackbar(
            response.data.message || "ส่งออก PDF ไม่สำเร็จ",
            "error"
          );
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการส่งออก", "error");
      } finally {
        this.exportLoading = false;
      }
    },

    closeReportViewer() {
      this.showReportViewer = false;
      this.report_govem_pdf = {};
    },

    closeExportDialog() {
      this.showExportDialog = false;
    },

    scrollToChart(chartId) {
      // Scroll functionality can be implemented here
    },

    editEmployee(employee) {
      this.editingId = employee.id_no;
      
      // Helper function สำหรับแปลงค่า
      const toInt = (val) => {
        if (!val || val === '' || val === '0' || val === 0) return null;
        const num = parseInt(val);
        return isNaN(num) ? null : num;
      };
      
      const toFloat = (val) => {
        if (!val || val === '' || val === '0' || val === 0) return null;
        const num = parseFloat(val);
        return isNaN(num) ? null : num;
      };
      
      // คัดลอกข้อมูลและแปลงค่าเป็นตัวเลข
      this.formData = {
        ...employee,
        brith_day: toInt(employee.brith_day),
        brith_month: toInt(employee.brith_month),
        brith_year: toInt(employee.brith_year),
        appoin_day: toInt(employee.appoin_day),
        appoin_month: toInt(employee.appoin_month),
        appoin_year: toInt(employee.appoin_year),
        salary_start: toFloat(employee.salary_start),
        salary_s: toFloat(employee.salary_s)
      };
      
      // Debug: แสดงข้อมูลที่แปลงแล้ว
      console.log('📝 Edit Employee Data:', {
        'วันเกิด': `${this.formData.brith_day}/${this.formData.brith_month}/${this.formData.brith_year}`,
        'วันบรรจุ': `${this.formData.appoin_day}/${this.formData.appoin_month}/${this.formData.appoin_year}`,
        'สัญญา': `${this.formData.last_contract_start} ถึง ${this.formData.last_contract_end}`,
        'ชนิดข้อมูล': {
          brith_year: typeof this.formData.brith_year,
          appoin_year: typeof this.formData.appoin_year
        }
      });
      
      this.showAddDialog = true;
      
      // Reset validation หลังจากโหลดข้อมูลเสร็จ
      this.$nextTick(() => {
        if (this.$refs.employeeForm) {
          this.$refs.employeeForm.resetValidation();
        }
      });
    },

    updateEmployee(employee) {
      return this.$router.push(
        "/college/gov_em/gov_emid/?id=" + employee.id_card
      );
    },

    viewEmployee(employee) {
      this.viewingEmployee = employee;
      this.showViewDialog = true;
    },

    confirmDelete(employee) {
      this.deletingEmployee = employee;
      this.showDeleteDialog = true;
    },

    async saveEmployee() {
      if (!this.$refs.employeeForm.validate()) {
        this.showSnackbar("กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง", "warning");
        return;
      }

      this.isSubmitting = true;

      try {
        // Upload file first if exists
        if (this.govem_contract_file) {
          await this.uploadContractFile(this.govem_contract_file);
        }

        if (this.formData.brith_date) {
          const birthDate = new Date(this.formData.brith_date);
          this.formData.brith_day = birthDate.getDate();
          this.formData.brith_month = birthDate.getMonth() + 1;
          this.formData.brith_year = birthDate.getFullYear() + 543;
        }

        if (this.formData.appoin_date) {
          const appointDate = new Date(this.formData.appoin_date);
          this.formData.appoin_day = appointDate.getDate();
          this.formData.appoin_month = appointDate.getMonth() + 1;
          this.formData.appoin_year = appointDate.getFullYear() + 543;
        }

        const endpoint = this.editingId
          ? "government_employee.update.php"
          : "government_employee.insert.php";

        const payload = { ...this.formData, ApiKey: this.ApiKey };
        if (this.editingId) {
          payload.id_no = this.editingId;
        }

        await this.$http.post(endpoint, payload);

        this.showSnackbar(
          this.editingId ? "อัปเดตข้อมูลสำเร็จ" : "เพิ่มข้อมูลสำเร็จ",
          "success"
        );

        this.closeDialog();
        await this.government_employeeQuery();
        this.initializeData();
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาด: " + error.message, "error");
      } finally {
        this.isSubmitting = false;
      }
    },

    async uploadContractFile(file) {
      this.isUploading = true;
      try {
        const idCard = this.formData.id_card || "unknown";
        const timestamp = Date.now().toString(16);
        const random = Math.random()
          .toString(16)
          .substring(2, 10);
        const ext = file.name.split(".").pop();
        const filename = `${timestamp}${random}_${idCard}.${ext}`;

        const formData = new FormData();
        formData.append("ApiKey", this.ApiKey);
        formData.append("file", file);
        formData.append("filename", "../HRemployee_contract/" + filename);

        const result = await this.$http.post("uploadfile.php", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (result.data && result.data.status) {
          this.formData.govem_contract = filename;
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        throw error;
      } finally {
        this.isUploading = false;
      }
    },

    async deleteEmployee() {
      this.isDeleting = true;
      try {
        let result = await this.$http.post(
          "government_employee.update.jor_govem_id_no.php",
          {
            ApiKey: this.ApiKey,
            id_no: this.deletingEmployee.id_no
          }
        );

        if (result.data == true) {
          this.showSnackbar("ลบข้อมูลสำเร็จ", "success");
          await this.government_employeeQuery();
          this.initializeData();
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาด: " + error.message, "error");
      } finally {
        this.showDeleteDialog = false;
        this.isDeleting = false;
      }
    },

    closeDialog() {
      this.showAddDialog = false;
      this.resetForm();
    },

    resetForm() {
      this.editingId = null;
      this.govem_contract_file = null;
      this.formData = {
        college_code: this.user.user_code || "",
        id_position: "",
        title_s: "",
        frist_name: "",
        last_name: "",
        id_card: "",
        sex: "",
        brith_date: "",
        brith_day: null,
        brith_month: null,
        brith_year: null,
        name_postion: "",
        type_position: "",
        marital_status: "",
        level_edd: "",
        ed_abb: "",
        ed_name: "",
        edd_university: "",
        appoin_date: "",
        appoin_day: null,
        appoin_month: null,
        appoin_year: null,
        salary_start: "",
        last_contract_start: "",
        last_contract_end: "",
        salary_s: "",
        govem_contract: "",
        province: ""
      };

      if (this.$refs.employeeForm) {
        this.$refs.employeeForm.resetValidation();
      }
    },

    formatCurrency(value) {
      if (!value) return "฿0.00";
      return (
        "฿" +
        parseFloat(value).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      );
    },

    formatCurrencyShort(value) {
      if (!value) return "฿0";
      const num = parseFloat(value);
      if (num >= 1000000) {
        return "฿" + (num / 1000000).toFixed(1) + "M";
      } else if (num >= 1000) {
        return "฿" + (num / 1000).toFixed(1) + "K";
      }
      return "฿" + num.toLocaleString("th-TH");
    },

    formatThaiDate(year, month, day) {
      if (!year || !month || !day) return "-";

      const months = [
        "ม.ค.",
        "ก.พ.",
        "มี.ค.",
        "เม.ย.",
        "พ.ค.",
        "มิ.ย.",
        "ก.ค.",
        "ส.ค.",
        "ก.ย.",
        "ต.ค.",
        "พ.ย.",
        "ธ.ค."
      ];

      const monthIndex = parseInt(month) - 1;
      if (monthIndex < 0 || monthIndex > 11) return "-";

      return `${parseInt(day)} ${months[monthIndex]} ${parseInt(year)}`;
    },

    showSnackbar(message, color = "success") {
      this.snackbar = { show: true, message, color, timeout: 3000 };
    }
  }
};
</script>

<style scoped>
/* Container Background */
.government-employee-container {
  background-color: #f4f7fc;
  min-height: 100vh;
  font-family: "Sarabun", sans-serif;
}

/* Gradient Cards */
.gradient-primary {
  background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%);
}

.gradient-success {
  background: linear-gradient(135deg, #059669 0%, #34d399 100%);
}

.gradient-warning {
  background: linear-gradient(135deg, #d97706 0%, #fbbf24 100%);
}

.gradient-info {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
}

/* Display Stat Numbers */
.display-stat {
  font-size: 2rem;
  line-height: 1;
}

/* Glass Effect Icon */
.glass-icon {
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

/* Decorative Elements */
.relative {
  position: relative;
}

.z-1 {
  position: relative;
  z-index: 1;
}

.absolute-circle {
  position: absolute;
  top: -30px;
  right: -30px;
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
}

/* Search Bar Shadow */
.search-bar >>> .v-input__slot {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04) !important;
}

/* Table Styles */
.custom-table >>> th {
  font-size: 0.9rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280 !important;
  background-color: #fff !important;
  border-bottom: 1px solid #f3f4f6 !important;
  white-space: nowrap;
}

.custom-table >>> td {
  font-size: 0.95rem !important;
  color: #374151;
  border-bottom: 1px solid #f3f4f6 !important;
  height: 70px !important;
}

.custom-table >>> tbody tr:hover {
  background-color: #f9fafb !important;
}

/* Form Styles */
.rounded-input >>> .v-input__slot {
  border-radius: 8px !important;
}

.bg-light-grey {
  background-color: #f9fafb;
}

.border-dashed {
  border: 1px dashed #bdbdbd;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

/* Opacity class */
.opacity-60 {
  opacity: 0.6;
}

.opacity-80 {
  opacity: 0.8;
}

/* Font Mono */
.font-mono {
  font-family: "Courier New", monospace;
}

/* Dashed divider */
.dashed {
  border-style: dashed !important;
}
</style>